import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPaginatedRouteList, searchRoutes } from '../../features/tripcenter/routeApi';
import {
    selectRouteList,
    setSelectedRoute,
    routePageIndexSelector,
    routesPerPageSelector,
    selectSearchQuery,
    setSearchQuery,
    selectPagination
} from '../../features/tripcenter/routeSlice';
import { List, TextField, InputAdornment, ListItem, Paper, Box, Typography, Divider } from '@mui/material';
import RouteListItem from './routeViewComponents/RouteListItem';
import Icon from '@mdi/react';
import { mdiFilter } from '@mdi/js';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import RouteIcon from '@mui/icons-material/RouteOutlined';
import RouteDetailView from './routeViewComponents/RouteDetailView';

import {
    selectSelectedRoute,
    selectIsDetailViewOpen,
    setDetailViewOpen,
} from '../../features/tripcenter/routeSlice';
const RoutesSideBarBody = () => {
    const dispatch = useDispatch();
    const routeList = useSelector(selectRouteList);
    const searchQuery = useSelector(selectSearchQuery);
    const selectedRoute = useSelector(selectSelectedRoute);
    const isDetailViewOpen = useSelector(selectIsDetailViewOpen);
    const routePageIndex = useSelector(routePageIndexSelector);
    const routesPerPage = useSelector(routesPerPageSelector);
    const pagination = useSelector(selectPagination);

    // Memoized filtered routes - only for client-side filtering when no search query
    const filteredRoutes = useMemo(() => {
        // If there's a search query, the API handles the filtering, so return the full routeList
        if (searchQuery) return routeList;

        // Only apply client-side filtering when there's no search query
        return routeList;
    }, [routeList, searchQuery]);

    useEffect(() => {
        if (searchQuery) {
            const offset = (routePageIndex - 1) * routesPerPage;
            dispatch(searchRoutes({ searchQuery, limit: routesPerPage, offset }));
        } else {
            const offset = (routePageIndex - 1) * routesPerPage;
            dispatch(getPaginatedRouteList({ limit: routesPerPage, offset }));
        }
        return () => {
            dispatch(setSelectedRoute(null));
        }
    }, [dispatch, routePageIndex, routesPerPage, searchQuery]);
    //console.log("routes", routeList);
    return (
        <div style={{ marginTop: '5px', position: 'relative', marginBottom: '30px' }}>
            <List dense>
                <ListItem
                    sx={{
                        borderRadius: 0.5,
                        mb: 0.15,
                        boxShadow: 1,
                        bgcolor: '#fafafa',
                        padding: 1,
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: '#fafafa',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Route Name, Number, Location"
                        value={searchQuery}
                        onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                '&:hover fieldset': {
                                    borderColor: 'primary.main',
                                },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Icon path={mdiFilter} size={0.8} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        minWidth: 'fit-content',
                        backgroundColor: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                    }}>
                        <span style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.875rem' }}>
                            {routeList.length > pagination?.limit ? pagination?.limit : routeList.length}
                        </span>
                        <span style={{ color: 'rgba(0, 0, 0, 0.38)', fontSize: '0.875rem' }}>
                            / {pagination?.total}
                        </span>
                    </div>
                </ListItem>
                {routeList && routeList.length > 0 ? (
                    routeList.map((route) => (
                        <RouteListItem
                            key={route._id}
                            route={route}
                        />
                    ))
                ) : (
                    <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{
                            m: 1,
                            p: 4,
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            border: '1px dashed',
                            borderColor: 'divider'
                        }}
                    >
                            {routeList && routeList?.length > 0 ? (
                            /* Show this when routes exist but filter returned no results */
                            <>
                                <SearchOffIcon sx={{ fontSize: 48, color: 'text.secondary' }} />

                                <Box>
                                    <Typography variant="h6" color="text.primary" fontWeight={500} gutterBottom>
                                        No Routes Found
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary">
                                        Try adjusting your search criteria
                                    </Typography>
                                </Box>

                                <Divider sx={{ width: '40%', my: 1 }} />

                                <Typography variant="caption" color="text.disabled">
                                    Modify your filter text to see available routes
                                </Typography>
                            </>
                        ) : (
                            /* Show this when no routes exist at all */
                            <>
                                <RouteIcon sx={{ fontSize: 48, color: 'text.secondary' }} />

                                <Box>
                                    <Typography variant="h6" color="text.primary" fontWeight={500} gutterBottom>
                                        No Routes Available
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary">
                                        You haven't created any routes yet
                                    </Typography>
                                </Box>

                                <Divider sx={{ width: '40%', my: 1 }} />

                                <Typography variant="caption" color="text.disabled">
                                    Use the "New Route" button above to create your first route
                                </Typography>
                            </>
                        )}
                    </Paper>
                )}
            </List>
            <RouteDetailView
                open={isDetailViewOpen}
                onClose={() => dispatch(setDetailViewOpen(false))}
                route={selectedRoute}
            />
        </div>
    );
};

export default RoutesSideBarBody;